<template>
  <b-modal
      ref="cmModal"
      centered
      size="lg"
      modal-class="comment-modal"
      header-class="comment-modal__header"
      body-class="comment-modal__body"
      footer-class="comment-modal__footer"
      hide-header-close
      :no-close-on-backdrop="isBusy"
      :no-close-on-esc="isBusy"
      @hidden="onHidden"
  >
    <template #modal-title>
      <div class="cm-title">{{ isEditing ? "ویرایش نظر" : "ایجاد نظر جدید" }}</div>
    </template>

    <b-form @submit.prevent>
      <div class="cm-section">
        <div class="cm-section__title">اطلاعات کاربر</div>

        <b-row class="cm-grid">
          <b-col cols="12" md="2">
            <div class="cm-avatar-box mt-1">
              <img v-if="form.imageUser" :src="form.imageUser" class="cm-avatar-img" alt="avatar" />
              <div v-else class="cm-avatar-placeholder">
                <span class="cm-avatar-placeholder__icon">👤</span>
              </div>

              <button
                  v-if="form.imageUser"
                  type="button"
                  class="cm-preview-close"
                  :disabled="avatar.isBtnsDisabled || isBusy"
                  @click="removeAvatarUploaded"
              >
                ×
              </button>
            </div>
          </b-col>

          <b-col cols="12" md="5">
            <b-form-group label="نام" label-for="c-firstName" label-class="cm-label" class="cm-group">
              <b-form-input
                  id="c-firstName"
                  v-model.trim="form.firstName"
                  class="cm-control"
                  :class="{ 'cm-control--invalid': !!errors.firstName }"
                  placeholder="نام خود را وارد کنید"
                  autocomplete="off"
                  :disabled="isBusy"
              />
              <div v-if="errors.firstName" class="cm-error">{{ errors.firstName }}</div>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="5">
            <b-form-group label="نام خانوادگی" label-for="c-lastName" label-class="cm-label" class="cm-group">
              <b-form-input
                  id="c-lastName"
                  v-model.trim="form.lastName"
                  class="cm-control"
                  :class="{ 'cm-control--invalid': !!errors.lastName }"
                  placeholder="نام خانوادگی خود را وارد کنید"
                  autocomplete="off"
                  :disabled="isBusy"
              />
              <div v-if="errors.lastName" class="cm-error">{{ errors.lastName }}</div>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="cm-grid">
          <b-col cols="12">
            <FileUploader
                title="آپلود آواتار"
                subtitle="فقط تصویر"
                accept="image/*"
                :file="avatar.file"
                :uploaded="avatar.uploaded"
                :uploaded-bytes="avatar.uploadedBytes"
                :total-size="avatar.totalSize"
                :speed-span="avatar.speedSpan"
                :eta-span="avatar.etaSpan"
                :done="avatar.done"
                :error-alert="avatar.errorAlert"
                :is-btns-disabled="avatar.isBtnsDisabled || isBusy"
                @select="onAvatarSelect"
                @upload="uploadAvatar"
                @reset="resetAvatar"
            />
          </b-col>
        </b-row>
      </div>

      <div class="cm-section">
        <div class="cm-section__title">موضوع نظر</div>

        <b-row class="cm-grid">
          <b-col cols="12" md="6">
            <b-form-group label="موضوع" label-for="c-type" label-class="cm-label" class="cm-group">
              <v-select
                  id="c-type"
                  label="name"
                  v-model="form.type"
                  :options="commentTypeOptions"
                  :reduce="(val) => val.value"
                  :clearable="false"
                  :disabled="isBusy"
                  class="cm-vselect"
              />
              <div v-if="errors.type" class="cm-error">{{ errors.type }}</div>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label-class="cm-label" class="cm-group">
              <div class="cm-picker" :class="{ 'cm-picker--invalid': !!errors.objectId }">
                <div v-if="Number(form.objectId) > 0 && !showPicker" class="cm-picker-selected">
                  <div class="cm-picker-selected__text">{{ selectedObjectLabel }}</div>
                  <button
                      type="button"
                      class="cm-picker-selected__clear"
                      :disabled="isBusy"
                      @click="clearObjectSelection"
                  >
                    ×
                  </button>
                </div>

                <div v-else>
                  <div v-if="Number(form.type) === 5">
                    <MasterPicker
                        :initialValue="isEditing ? { userId: form.objectId, fullName: form.objectTitle } : null"
                        @getMaster="setSelectedMaster"
                    />
                  </div>

                  <div v-else-if="Number(form.type) === 6">
                    <LessonCategoryPicker
                        closeOnSelect
                        :initialValue="isEditing ? { id: form.objectId, title: form.objectTitle } : null"
                        @getLessonCategory="setSelectedPackageCategory"
                    />
                  </div>

                  <div v-else-if="Number(form.type) === 3">
                    <LessonPicker
                        closeOnSelect
                        :initialValue="isEditing ? { lessonId: form.objectId, title: form.objectTitle } : null"
                        @getLesson="setSelectedLesson"
                    />
                  </div>

                  <div v-else class="cm-hint">ابتدا موضوع را انتخاب کنید.</div>
                </div>
              </div>

              <div v-if="errors.objectId" class="cm-error">{{ errors.objectId }}</div>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div class="cm-section">
        <div class="cm-section__title">متن نظر</div>

        <b-row class="cm-grid">
          <b-col cols="12">
            <b-form-group label="متن" label-for="c-message" label-class="cm-label" class="cm-group">
              <b-form-textarea
                  id="c-message"
                  rows="4"
                  v-model.trim="form.message"
                  class="cm-control cm-textarea"
                  :class="{ 'cm-control--invalid': !!errors.content }"
                  placeholder="متن نظر را وارد کنید..."
                  :disabled="isBusy"
              />
              <div v-if="errors.content" class="cm-error">{{ errors.content }}</div>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div class="cm-section">
        <div class="cm-section__title">مدیا</div>

        <b-row class="cm-grid">
          <b-col cols="12" md="6">
            <b-form-group label="نوع مدیا" label-for="c-mediaType" label-class="cm-label" class="cm-group">
              <v-select
                  id="c-mediaType"
                  label="name"
                  v-model="form.mediaType"
                  :options="mediaTypeOptionsUI"
                  :reduce="(val) => val.value"
                  :clearable="true"
                  placeholder="انتخاب مدیا"
                  class="cm-vselect"
                  :disabled="isBusy"
              />
              <div v-if="errors.mediaType" class="cm-error">{{ errors.mediaType }}</div>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <FileUploader
                title="آپلود مدیا"
                :subtitle="mediaSubtitle"
                :accept="fileAccept"
                :file="media.file"
                :uploaded="media.uploaded"
                :uploaded-bytes="media.uploadedBytes"
                :total-size="media.totalSize"
                :speed-span="media.speedSpan"
                :eta-span="media.etaSpan"
                :done="media.done"
                :error-alert="media.errorAlert"
                :is-btns-disabled="media.isBtnsDisabled || isBusy || !canUploadMedia"
                @select="onMediaSelect"
                @upload="uploadMedia"
                @reset="removeMediaUploaded"
            />

            <div v-if="!canUploadMedia" class="cm-hint-box mt-2">
              برای آپلود، یکی از گزینه‌های تصویر/صوت/ویدیو را انتخاب کنید.
            </div>
          </b-col>

          <b-col cols="12" v-if="effectiveMediaUrl">
            <div class="cm-preview mt-2">
              <div class="cm-preview__head">
                <div class="cm-preview__head-left">
                  پیش‌نمایش مدیا
                  <span class="cm-chip">{{ effectiveMediaLabel }}</span>
                </div>

                <button
                    type="button"
                    class="cm-preview__remove"
                    :disabled="isBusy"
                    @click="removeMediaUploaded"
                >
                  حذف مدیا
                </button>
              </div>

              <div class="cm-preview__body">
                <img v-if="Number(effectiveMediaType) === 2" :src="effectiveMediaUrl" class="cm-media" alt="" />
                <audio v-else-if="Number(effectiveMediaType) === 3" class="w-100" controls :src="effectiveMediaUrl" />
                <video v-else-if="Number(effectiveMediaType) === 4" class="cm-media" controls :src="effectiveMediaUrl" />
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-form>

    <template #modal-footer>
      <div class="cm-footer">
        <b-button type="button" variant="outline-secondary" class="cm-btn cm-btn--ghost" :disabled="isBusy" @click="closeModal">
          انصراف
        </b-button>

        <b-button type="button" variant="success" class="cm-btn cm-btn--success" :disabled="submitDisabled" @click="submit">
          <span v-if="isBusy" class="cm-spinner"></span>
          {{ isBusy ? "در حال ثبت..." : isEditing ? "ویرایش نظر" : "ثبت نظر" }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from "axios"
import vSelect from "vue-select"
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue"
import MasterPicker from "@/views/components/MasterActiveCourse/MasterPicker.vue"
import LessonPicker from "@/views/components/Lessons/LessonPicker.vue"
import LessonCategoryPicker from "@/views/components/LessonCategories/LessonCategoryPicker.vue"
import FileUploader from "@/views/components/Utils/CustomUploadMedia.vue"
import { BModal, BForm, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BButton } from "bootstrap-vue"

export default {
  name: "CommentCreateEditModal",
  model: { prop: "value", event: "input" },
  components: {
    vSelect,
    MasterPicker,
    LessonPicker,
    LessonCategoryPicker,
    FileUploader,
    BModal,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
  },
  props: {
    value: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    editingItem: { type: Object, default: null },
    commentTypeOptions: { type: Array, default: () => [] },
    mediaTypeOptionsUI: { type: Array, default: () => [] },
  },
  data() {
    return {
      localSubmitting: false,
      isHydrating: false,
      showPicker: false,
      form: this.emptyForm(),
      avatar: this.emptyUploadState(),
      media: this.emptyUploadState(),
      errors: this.emptyErrors(),
    }
  },
  computed: {
    isEditing() {
      return !!(this.editingItem && Number(this.editingItem.commentId || 0))
    },
    isBusy() {
      return !!(this.busy || this.localSubmitting || this.avatar.isBtnsDisabled || this.media.isBtnsDisabled)
    },
    canUploadMedia() {
      return [2, 3, 4].includes(Number(this.form.mediaType))
    },
    fileAccept() {
      const mt = Number(this.form.mediaType)
      if (mt === 2) return "image/*"
      if (mt === 3) return "audio/*"
      if (mt === 4) return "video/*"
      return ""
    },
    mediaLabel() {
      const mt = Number(this.form.mediaType)
      if (mt === 2) return "تصویر"
      if (mt === 3) return "صوت"
      if (mt === 4) return "ویدئو"
      return "-"
    },
    mediaSubtitle() {
      if (!this.canUploadMedia) return "نوع مدیا را انتخاب کنید"
      return this.mediaLabel
    },
    uploaderFileTypeForMedia() {
      const mt = Number(this.form.mediaType)
      if (mt === 2) return 4
      if (mt === 3) return 3
      if (mt === 4) return 3
      return 0
    },
    uploaderFileTypeForAvatar() {
      return 4
    },
    selectedObjectLabel() {
      const t = Number(this.form.type)
      const id = Number(this.form.objectId || 0)
      if (!t || !id) return ""
      const title = String(this.form.objectTitle || "").trim()
      if (title) return title
      if (t === 5) return "استاد"
      if (t === 6) return "پکیج"
      if (t === 3) return "درس"
      return `#${id}`
    },
    submitDisabled() {
      if (this.isBusy) return true
      return false
    },
    effectiveMediaUrl() {
      return String(this.form.mediaUrl || "").trim()
    },
    effectiveMediaType() {
      const url = this.effectiveMediaUrl
      if (!url) return 1
      const mt = Number(this.form.mediaType || 0)
      return [2, 3, 4].includes(mt) ? mt : 2
    },
    effectiveMediaLabel() {
      const mt = Number(this.effectiveMediaType)
      if (mt === 2) return "تصویر"
      if (mt === 3) return "صوت"
      if (mt === 4) return "ویدئو"
      return "-"
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.$nextTick(() => {
          if (!this.$refs.cmModal) return
          if (v) this.$refs.cmModal.show()
          else this.$refs.cmModal.hide()
        })
      },
    },
    busy(v) {
      if (!v) this.localSubmitting = false
    },
    editingItem: {
      immediate: true,
      handler(v) {
        if (!v) {
          this.resetAll()
          return
        }
        this.hydrateFromItem(v)
      },
    },
    "form.type"() {
      if (this.isHydrating) return
      this.showPicker = true
      this.clearObjectSelection()
    },
    "form.mediaType"() {
      if (this.isHydrating) return
      this.errors.mediaType = ""
      this.resetMedia()
    },
    "form.firstName"() {
      if (this.errors.firstName) this.errors.firstName = ""
    },
    "form.message"() {
      if (this.errors.content) this.errors.content = ""
    },
    "form.mediaUrl"() {
      if (this.errors.content) this.errors.content = ""
      if (this.errors.mediaType) this.errors.mediaType = ""
    },
  },
  methods: {
    emptyForm() {
      return {
        firstName: "",
        lastName: "",
        imageUser: "",
        message: "",
        mediaUrl: "",
        objectId: 0,
        objectTitle: "",
        type: 5,
        mediaType: null,
      }
    },
    emptyErrors() {
      return { firstName: "", lastName: "", type: "", objectId: "", content: "", mediaType: "" }
    },
    emptyUploadState() {
      return {
        file: null,
        uploaded: 0,
        uploadedBytes: 0,
        totalSize: 0,
        speedSpan: 0,
        etaSpan: 0,
        done: null,
        errorAlert: false,
        isBtnsDisabled: false,
        _lastTime: 0,
        _lastLoaded: 0,
      }
    },
    resetAll() {
      this.localSubmitting = false
      this.isHydrating = false
      this.showPicker = true
      this.form = this.emptyForm()
      this.avatar = this.emptyUploadState()
      this.media = this.emptyUploadState()
      this.errors = this.emptyErrors()
    },
    onHidden() {
      this.$emit("input", false)
      this.resetAll()
      this.$emit("hidden")
    },
    closeModal() {
      if (this.isBusy) return
      this.$emit("input", false)
      if (this.$refs.cmModal) this.$refs.cmModal.hide()
    },
    pickId(obj, keys) {
      for (const k of keys) {
        const n = Number(obj?.[k] ?? 0)
        if (Number.isFinite(n) && n > 0) return n
      }
      return 0
    },
    pickTitle(obj, keys) {
      for (const k of keys) {
        const t = String(obj?.[k] ?? "").trim()
        if (t) return t
      }
      return ""
    },
    normalizeType(t) {
      const n = Number(t || 0)
      return [3, 5, 6].includes(n) ? n : 5
    },
    hydrateFromItem(item) {
      this.isHydrating = true
      this.errors = this.emptyErrors()
      this.showPicker = false

      const type = this.normalizeType(item?.type || 5)
      const objectId = Number(item?.objectId || 0) || 0
      const objectTitle = String(item?.objectTitle || "").trim() || ""

      const mt = Number(item?.mediaType || 1)
      const safeMt = [1, 2, 3, 4].includes(mt) ? mt : 1

      this.form = {
        firstName: String(item?.firstName || ""),
        lastName: String(item?.lastName || ""),
        imageUser: String(item?.imageUser || ""),
        message: String(item?.message || ""),
        mediaUrl: String(item?.mediaUrl || ""),
        objectId,
        objectTitle,
        type,
        mediaType: safeMt === 1 ? null : safeMt,
      }

      this.$nextTick(() => {
        this.isHydrating = false
      })
    },
    clearObjectSelection() {
      this.form.objectId = 0
      this.form.objectTitle = ""
      this.errors.objectId = ""
      this.showPicker = true
    },
    setSelectedMaster(master) {
      const id = this.pickId(master, ["userId", "id", "masterId", "teacherId"])
      const title = this.pickTitle(master, ["fullName", "name", "title"])
      this.form.objectId = id
      this.form.objectTitle = title || (id ? `استاد ${id}` : "")
      this.showPicker = false
      if (id) this.errors.objectId = ""
    },
    setSelectedLesson(payload) {
      const id = typeof payload === "number" ? Number(payload) : this.pickId(payload, ["lessonId", "id", "value", "courseLessonId"])
      const title = typeof payload === "number" ? "" : this.pickTitle(payload, ["title"])
      this.form.objectId = id
      this.form.objectTitle = title || (id ? `درس ${id}` : "")
      this.showPicker = false
      if (id) this.errors.objectId = ""
    },
    setSelectedPackageCategory(payload) {
      const id = typeof payload === "number" ? Number(payload) : this.pickId(payload, ["id", "value", "lessonCategoryId", "categoryId", "packageId"])
      const title = typeof payload === "number" ? "" : this.pickTitle(payload, ["title"])
      this.form.objectId = id
      this.form.objectTitle = title || (id ? `پکیج ${id}` : "")
      this.showPicker = false
      if (id) this.errors.objectId = ""
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === "danger" ? "AlertTriangleIcon" : "CheckIcon",
          variant,
        },
      })
    },
    validate(showToast = true) {
      const base = this.editingItem || {}
      const errs = this.emptyErrors()

      const firstName = String(this.form.firstName || "").trim()
      const type = this.normalizeType(this.form.type)
      const objectId = Number(this.form.objectId || 0)
      const msg = String(this.form.message || "").trim()
      const mediaUrl = String(this.form.mediaUrl || "").trim()

      if (!firstName) errs.firstName = "نام الزامی است."
      if (![3, 5, 6].includes(Number(type))) errs.type = "موضوع معتبر نیست."
      if (!objectId) errs.objectId = "انتخاب استاد/درس/پکیج الزامی است."
      if (!msg && !mediaUrl) errs.content = "حداقل یکی از متن یا مدیا باید وارد شود."

      if (mediaUrl) {
        const mt = Number(this.form.mediaType || 0)
        if (![2, 3, 4].includes(mt)) errs.mediaType = "برای مدیا، نوع مدیا الزامی است."
      }

      this.errors = errs

      const ok = !Object.values(errs).some(Boolean)
      if (!ok && showToast) {
        const firstErr = Object.values(errs).find((x) => !!x)
        this.toast(firstErr || "اطلاعات را کامل کنید.", "danger")
      }
      return ok
    },
    onAvatarSelect(file) {
      this.avatar.file = file || null
      this.avatar.done = null
      this.avatar.errorAlert = false
    },
    onMediaSelect(file) {
      this.media.file = file || null
      this.media.done = null
      this.media.errorAlert = false
      if (file && !this.canUploadMedia) {
        this.toast("ابتدا نوع مدیا را انتخاب کنید.", "danger")
      }
    },
    resetAvatar() {
      this.avatar = { ...this.emptyUploadState(), file: null }
    },
    resetMedia() {
      this.media = { ...this.emptyUploadState(), file: null }
    },
    removeAvatarUploaded() {
      if (this.isBusy) return
      this.form.imageUser = ""
      this.resetAvatar()
    },
    removeMediaUploaded() {
      if (this.isBusy) return
      this.form.mediaUrl = ""
      this.form.mediaType = null
      this.resetMedia()
      this.errors.mediaType = ""
    },
    buildUploadFormData(file, fileType, meta) {
      const fd = new FormData()
      fd.append("ImageFile", file)
      fd.append("FileType", String(fileType))
      fd.append("Title", String(meta?.title || ""))
      fd.append("Alt", String(meta?.alt || ""))
      fd.append("Description", String(meta?.description || ""))
      return fd
    },
    updateProgress(state, progressEvent) {
      const total = Number(progressEvent?.total || 0)
      const loaded = Number(progressEvent?.loaded || 0)
      if (!total) return

      const now = Date.now()
      const dt = state._lastTime ? Math.max(0.001, (now - state._lastTime) / 1000) : 0
      const dLoaded = state._lastTime ? Math.max(0, loaded - state._lastLoaded) : 0
      const speedBytes = dt ? dLoaded / dt : 0
      const speedKB = speedBytes / 1024

      state.totalSize = total
      state.uploadedBytes = Math.min(loaded, total)
      state.uploaded = (state.uploadedBytes / total) * 100
      state.speedSpan = speedKB > 0 ? speedKB : state.speedSpan

      const remaining = Math.max(0, total - state.uploadedBytes)
      state.etaSpan = speedBytes > 0 ? remaining / speedBytes : state.etaSpan

      state._lastTime = now
      state._lastLoaded = loaded
    },
    async uploadRequest(formData, state) {
      const url = "/Uploader/Upload"
      state.isBtnsDisabled = true
      state.errorAlert = false
      state.done = null
      state.uploaded = 0
      state.uploadedBytes = 0
      state.totalSize = 0
      state.speedSpan = 0
      state.etaSpan = 0
      state._lastTime = 0
      state._lastLoaded = 0

      const res = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => this.updateProgress(state, e),
      })

      state.uploaded = 100
      state.done = "آپلود با موفقیت انجام شد."
      return res
    },
    extractUploadedUrl(res) {
      const r = res?.data ?? res
      if (r?.url) return String(r.url).trim()
      if (r?.data?.url) return String(r.data.url).trim()
      if (r?.data?.data?.url) return String(r.data.data.url).trim()
      return ""
    },
    async uploadAvatar() {
      try {
        const file = this.avatar.file
        if (!file) return

        const meta = { title: "آواتار", alt: "آواتار", description: "آواتار" }
        const fd = this.buildUploadFormData(file, this.uploaderFileTypeForAvatar, meta)

        const res = await this.uploadRequest(fd, this.avatar)
        const url = this.extractUploadedUrl(res)
        if (!url) {
          this.avatar.errorAlert = true
          this.toast("آپلود آواتار انجام نشد.", "danger")
          return
        }

        this.form.imageUser = url
      } catch (e) {
        this.avatar.errorAlert = true
        this.toast("خطا در آپلود آواتار.", "danger")
      } finally {
        this.avatar.isBtnsDisabled = false
        this.avatar.file = null
      }
    },
    async uploadMedia() {
      try {
        if (!this.canUploadMedia) {
          this.toast("نوع مدیا را انتخاب کنید.", "danger")
          return
        }

        const file = this.media.file
        if (!file) return

        const fileType = this.uploaderFileTypeForMedia
        if (!fileType) return

        const meta = { title: this.mediaLabel, alt: this.mediaLabel, description: this.mediaLabel }
        const fd = this.buildUploadFormData(file, fileType, meta)

        const res = await this.uploadRequest(fd, this.media)
        const url = this.extractUploadedUrl(res)
        if (!url) {
          this.media.errorAlert = true
          this.toast("آپلود فایل انجام نشد.", "danger")
          return
        }

        this.form.mediaUrl = url
        this.errors.mediaType = ""
      } catch (e) {
        this.media.errorAlert = true
        this.toast("خطا در آپلود فایل.", "danger")
      } finally {
        this.media.isBtnsDisabled = false
        this.media.file = null
      }
    },
    buildSubmitPayload() {
      const base = this.editingItem || {}
      const type = this.normalizeType(this.form.type)
      const objectId = Number(this.form.objectId || 0)

      const firstName = String(this.form.firstName || "").trim()
      const lastName = String(this.form.lastName || "").trim()
      const imageUser = String(this.form.imageUser || "").trim()
      const message = String(this.form.message || "").trim()
      const mediaUrl = String(this.form.mediaUrl || "").trim()
      const mediaType = mediaUrl ? Number(this.form.mediaType || 0) : 1

      return {
        commentId: this.isEditing ? Number(base.commentId || 0) : 0,
        parentId: this.isEditing ? Number(base.parentId || 0) : 0,
        firstName,
        lastName,
        imageUser,
        message,
        objectId,
        type,
        mediaType: [1, 2, 3, 4].includes(Number(mediaType)) ? Number(mediaType) : 1,
        createDate: this.isEditing ? (base.createDate || new Date().toISOString()) : new Date().toISOString(),
        userId: this.isEditing ? Number(base.userId || 0) : 0,
        mediaUrl,
        objectTitle: this.form.objectTitle || "",
      }
    },
    submit() {
      if (this.isBusy) return
      const ok = this.validate(true)
      if (!ok) return
      this.localSubmitting = true
      this.$emit("submit", this.buildSubmitPayload())
    },
  },
}
</script>

<style scoped>
.comment-modal__header {
  border-bottom: 0;
  padding: 16px 22px 10px;
}
.comment-modal__body {
  padding: 12px 22px 8px;
}
.comment-modal__footer {
  border-top: 0;
  padding: 12px 22px 18px;
}
.comment-modal :deep(.modal-content) {
  border: 0;
  border-radius: 16px;
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  direction: rtl;
}

.cm-title {
  font-size: 18px;
  font-weight: 900;
  color: #111827;
}

.cm-section {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 12px;
}
.cm-section__title {
  font-size: 13px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 10px;
}
.cm-grid {
  margin-top: 2px;
}
.cm-group :deep(.form-group) {
  margin-bottom: 0 !important;
}
.cm-label {
  font-size: 12px;
  font-weight: 800;
  color: #374151;
  margin-bottom: 6px;
  min-height: 18px;
  display: flex;
  align-items: center;
}

.cm-control {
  height: 44px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 10px 12px;
}
.cm-textarea {
  height: auto;
  min-height: 120px;
  resize: vertical;
}

.cm-control--invalid {
  border-color: #ef4444 !important;
}

.cm-error {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 800;
  color: #ef4444;
}

.cm-vselect :deep(.vs__dropdown-toggle) {
  height: 44px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 2px 10px;
  display: flex;
  align-items: center;
}

.cm-picker {
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
  padding: 10px;
  background: #fafafa;
  min-height: 44px;
}
.cm-picker--invalid {
  border-color: #ef4444;
}
.cm-hint {
  font-size: 12px;
  color: #6b7280;
}
.cm-hint-box {
  min-height: 44px;
  display: flex;
  align-items: center;
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
  padding: 0 12px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 12px;
}

.cm-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.cm-btn {
  height: 44px;
  border-radius: 12px;
  font-weight: 900;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}
.cm-btn--success {
  box-shadow: 0 10px 24px rgba(16, 185, 129, 0.22);
}
.cm-btn--ghost {
  background: transparent;
}

.cm-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.55);
  border-top-color: rgba(255, 255, 255, 1);
  border-radius: 999px;
  display: inline-block;
  animation: cmspin 0.9s linear infinite;
}
@keyframes cmspin {
  to {
    transform: rotate(360deg);
  }
}

.cm-preview {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  background: #0b1220;
}
.cm-preview__head {
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 900;
  color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.06);
}
.cm-preview__head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cm-preview__remove {
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}
.cm-chip {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.cm-preview__body {
  padding: 10px;
}
.cm-media {
  width: 100%;
  max-height: 320px;
  object-fit: contain;
  border-radius: 12px;
  background: transparent;
}

.cm-avatar-box {
  position: relative;
  width: 56px;
  height: 56px;
}
.cm-avatar-img {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
  background: #fff;
}
.cm-avatar-placeholder {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cm-avatar-placeholder__icon {
  font-size: 22px;
  opacity: 0.65;
}
.cm-preview-close {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  font-size: 16px;
  line-height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cm-picker-selected {
  min-height: 44px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.cm-picker-selected__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 900;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cm-picker-selected__clear {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  font-size: 18px;
  line-height: 24px;
  cursor: pointer;
  flex: 0 0 auto;
}
</style>